import { FileType, type LoadedFile, type ProcessFileRules } from '@/core/types/files';

export const imageExtensions = '.png, .jpg, .jpeg';
export const videoExtensions =
  '.mp4, .webm, .mkv, .flv, .vob, .gif, .avi, .mov, .wmv, .rm, .rmvb, .amv, .mpg, .mpeg, .nsv';
export const fileExtensions = '.pdf, .txt, .doc, .odt, .docx';
export const pdfExtensions = '.pdf';

export const getFileExtension = (fileName: string) => fileName.split('.').pop();

export const isImageExtension = (extension: string) => imageExtensions.includes(extension);

export const isPdfExtension = (extension: string) => pdfExtensions.includes(extension);

export const getImageLocalUrl = (file: Blob): string => {
  const result = window.URL.createObjectURL(file);
  if (typeof result === 'string') {
    return result;
  }

  throw new Error('Failed to get string from the file reader');
};

export const getBiteFromMb = (size: number) => size * 1048576;
export const getMbFromBite = (size: number) => size / 1048576;

export const FILE_UNITS = ['b', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb'] as const;
export const FILE_SIZE_DIVISOR = 6.931471805599453; // result of Math.log(1024)

export const formatBytes = (bytes = 0, decimals = 2): string => {
  const correctDecimals = decimals < 0 ? 0 : decimals;
  const index = Math.floor(Math.log(Math.abs(bytes) || 1) / FILE_SIZE_DIVISOR);
  const size = parseFloat((bytes / Math.pow(1024, index)).toFixed(correctDecimals));

  return `${size} ${FILE_UNITS[index]}`;
};

export const checkFileIsImage = (file: File) => file.type.includes('image');

export const checkFileMimeType = (file: File, accept: string) => {
  const ext = getFileExtension(file.name);

  if (!ext) {
    return false;
  }

  return accept.includes(`.${ext}`);
};

export const isIamgeFile = (file: File) => {
  const extension = `.${getFileExtension(file.name)}`.toLowerCase();

  return isImageExtension(extension);
};

export const processFile = async (file: File, rules?: ProcessFileRules): Promise<LoadedFile> => {
  let error: string | undefined;

  const extension = `.${getFileExtension(file.name)}`.toLowerCase();

  if (file.size === 0) {
    error = 'Недопустимый формат файла';
  }

  if (rules && !error) {
    if (rules.extension) {
      if (!rules.extension.includes(extension)) {
        error = 'Недопустимый формат файла';
      }
    }

    if (!error && rules.maxSize && file.size > rules.maxSize) {
      error = 'Слишком большой размер файла';
    }

    if (!error && rules.custom) {
      error = await rules.custom(file);
    }
  }

  const url = error ? undefined : getImageLocalUrl(file);

  const type = error
    ? FileType.OTHER
    : isImageExtension(extension)
      ? FileType.IMAGE
      : isPdfExtension(extension)
        ? FileType.PDF
        : FileType.OTHER;

  return {
    id: Math.random().toString(36).substring(2, 15),
    file,
    name: file.name,
    extension,
    type,
    size: file.size,
    url,
    error: error || '',
    loading: false,
    uploaded: false,
  };
};

export const getBlobFromUrl = (url: string) => fetch(url).then((res) => res.blob());

export const createUrlFromBlob = (blob: Blob) => URL.createObjectURL(blob);

export const openDocument = (content: string, fileName: string, mimeType = 'application/pdf') => {
  const url = createUrlFromBlob(new Blob([content], { type: mimeType }));

  const pdfWindow = window.open(url, '_blank');
  if (pdfWindow) {
    pdfWindow.onload = () => {
      setTimeout(() => {
        pdfWindow.document.title = fileName;
      }, 500);
    };
  }

  URL.revokeObjectURL(url);
};

export const downloadDocumentFromUrl = (url: string, fileName: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', fileName);
  document.body.appendChild(a);
  a.click();
};

export const downloadDocument = async (content: Blob, fileName: string, mimeType = 'application/pdf') => {
  const url = createUrlFromBlob(new Blob([content], { type: mimeType }));

  await downloadDocumentFromUrl(url, fileName);
};

export const downloadDocumentByUrl = async (url: string, fileName: string) => {
  const blob = await getBlobFromUrl(url);

  downloadDocumentFromUrl(createUrlFromBlob(blob), `${fileName}.${blob.type.split('/')[1]}`);
};

export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Returns HTML image element loaded from the src.
 */
export async function loadImage(src: string) {
  const image = new Image();
  image.src = src;

  return new Promise<HTMLImageElement>((resolve, reject) => {
    image.addEventListener('load', () => {
      // decode image to get ability to use it in canvas right after loading
      // https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/decode
      // ignore decoding error
      decodeImage(image).then(resolve).catch(resolve);
    });
    image.addEventListener('error', (event) => {
      const errorMessage = event.message || `Failed to load image ${src}`;
      const error = new Error(errorMessage);
      reject(error);
    });
  });
}

export function decodeImage(image: HTMLImageElement) {
  return new Promise<HTMLImageElement>((resolve) => {
    image
      .decode()
      .then(() => {
        resolve(image);
      })
      .catch((e) => {
        console.error(e);
        // ignore decoding error
        resolve(image);
      });
  });
}
